import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useTransports, useCreateTransport, useUpdateTransport, useDeleteTransport } from "@/hooks/use-transports";
import type { Transport } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";
import { useServerStatus } from "@/hooks/use-server-status";

const defaultTransport = {
  name: "",
  protocol: "udp",
  bind: null as string | null,
  local_net: null as string | null,
  external_media_address: null as string | null,
  external_signaling_address: null as string | null,
  external_signaling_port: 0,
  tos: null as string | null,
  cos: 0,
  allow_reload: false,
  allow_wildcard_certs: false,
  symmetric_transport: false,
  domain: null as string | null,
  cert_file: null as string | null,
  priv_key_file: null as string | null,
  ca_list_file: null as string | null,
  cipher: null as string | null,
  method: null as string | null,
  password: null as string | null,
  verify_client: null as string | null,
  verify_server: null as string | null,
  require_client_cert: false,
  tcp_keepalive_enable: false,
  tcp_keepalive_idle_time: 0,
  tcp_keepalive_interval_time: 0,
  tcp_keepalive_probe_count: 0,
  websocket_write_timeout: 0,
};

const columns: Column<Transport>[] = [
  { key: "name", header: "Name" },
  {
    key: "protocol",
    header: "Protocol",
    render: (t) => <span className="font-mono text-xs uppercase">{t.protocol}</span>,
  },
  { key: "bind", header: "Bind" },
  { key: "external_media_address", header: "External Media" },
];

export default function TransportsPage() {
  const { data, isLoading, isFetching, error, refetch } = useTransports();
  const createMut = useCreateTransport();
  const updateMut = useUpdateTransport();
  const deleteMut = useDeleteTransport();
  const { data: status } = useServerStatus();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Transport | null>(null);
  const [form, setForm] = useState(defaultTransport);
  const [showTls, setShowTls] = useState(false);
  const [showTcp, setShowTcp] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Transport | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.protocol.toLowerCase().includes(q) ||
        (t.bind ?? "").toLowerCase().includes(q)
    );
  }, [list, query]);

  const openCreate = () => {
    setEditing(null);
    const natAddr = status?.public_url?.replace(/^sip:/, "") || status?.public_ip || null;
    setForm({ ...defaultTransport, external_signaling_address: natAddr, external_media_address: natAddr, bind: "0.0.0.0:5060" });
    setShowTls(false);
    setShowTcp(false);
    setOpen(true);
  };

  const openEdit = (t: Transport) => {
    setEditing(t);
    const { id, created_at, updated_at, ...rest } = t;
    setForm({ ...rest });
    setShowTls(rest.protocol === "tls" || rest.protocol === "wss");
    setShowTcp(rest.protocol === "tcp" || rest.protocol === "tls" || rest.protocol === "ws" || rest.protocol === "wss");
    setOpen(true);
  };

  const handleSave = () => {
    const cleanForm = { ...form };
    (Object.keys(cleanForm) as (keyof typeof cleanForm)[]).forEach((k) => {
      if (cleanForm[k] === null) (cleanForm as Record<string, unknown>)[k] = null;
    });

    if (editing) {
      updateMut.mutate(
        { id: editing.id, data: cleanForm as Partial<typeof cleanForm> },
        {
          onSuccess: () => { setOpen(false); toast.success("Transport updated"); },
          onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
        }
      );
    } else {
      createMut.mutate(cleanForm as typeof cleanForm, {
        onSuccess: () => { setOpen(false); toast.success("Transport created"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const handleDelete = (t: Transport) => {
    deleteMut.mutate(t.id, {
      onSuccess: () => { toast.success("Transport deleted"); setPendingDelete(null); },
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value } as typeof prev;
      if (field === "protocol") {
        const proto = value as string;
        setShowTls(proto === "tls");
        setShowTcp(proto === "tcp" || proto === "tls");
        if (!editing) {
          next.bind = proto === "tls" ? "0.0.0.0:5061" : "0.0.0.0:5060";
        }
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Transports" description="PJSIP transport configurations" />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search transports..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={
          <Button onClick={openCreate} size="sm">
            <Plus size={14} /> Create Transport
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(t) => t.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No transports configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Transport" : "Create Transport"}
        description="How Asterisk listens for and sends SIP traffic."
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>
              {editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <FormSection title="General" columns={2}>
            <Field label="Name" required htmlFor="name" info="Unique transport identifier. Used in [transport_name] config section. Example: udp-transport, tls-transport.">
              <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
            </Field>
            <Field label="Protocol" htmlFor="protocol" info="SIP transport protocol. udp (default) for most trunks. tcp for reliable transport. tls for encrypted signaling.">
              <Select id="protocol" value={form.protocol} onChange={(e) => updateField("protocol", e.target.value)}>
                <option value="udp">UDP</option>
                <option value="tcp">TCP</option>
                <option value="tls">TLS</option>
              </Select>
            </Field>
            <Field label="Bind Address" htmlFor="bind" info="IP:port to listen on. Default 0.0.0.0:5060 (all interfaces). Example: 192.168.1.10:5060 to bind to a specific NIC.">
              <Input id="bind" value={form.bind ?? ""} onChange={(e) => updateField("bind", e.target.value || null)} placeholder="0.0.0.0:5060" />
            </Field>
            <Field label="Local Net" htmlFor="local_net" info="This should be your VPC CIDR. CIDR ranges identifying local networks. Used with NAT to avoid rewriting local addresses. Example: 192.168.0.0/16,10.0.0.0/8.">
              <Input id="local_net" value={form.local_net ?? ""} onChange={(e) => updateField("local_net", e.target.value || null)} placeholder="192.168.0.0/16" />
            </Field>
          </FormSection>

          <FormSection title="NAT" columns={2}>
            <Field label="External Media Address" htmlFor="external_media_address" info="Public IP that RTP/audio should use when behind NAT. Set this if Asterisk is behind a NAT/firewall. Example: 203.0.113.50.">
              <Input id="external_media_address" value={form.external_media_address ?? ""} onChange={(e) => updateField("external_media_address", e.target.value || null)} />
            </Field>
            <Field label="External Signaling Address" htmlFor="external_signaling_address" info="Public IP for SIP signaling when behind NAT. Example: 203.0.113.50.">
              <Input id="external_signaling_address" value={form.external_signaling_address ?? ""} onChange={(e) => updateField("external_signaling_address", e.target.value || null)} />
            </Field>
            <Field label="External Signaling Port" htmlFor="external_signaling_port" info="External port for signaling when different from bind port. Default 0 = use bind port.">
              <Input type="number" id="external_signaling_port" value={form.external_signaling_port} onChange={(e) => updateField("external_signaling_port", Number(e.target.value))} />
            </Field>
          </FormSection>

          {showTls && (
            <FormSection title="TLS" columns={2}>
              <Field label="Cert File" htmlFor="cert_file" info="Path to TLS certificate file. Example: /etc/asterisk/certs/asterisk.crt.">
                <Input id="cert_file" value={form.cert_file ?? ""} onChange={(e) => updateField("cert_file", e.target.value || null)} />
              </Field>
              <Field label="Private Key File" htmlFor="priv_key_file" info="Path to TLS private key. Example: /etc/asterisk/certs/asterisk.key.">
                <Input id="priv_key_file" value={form.priv_key_file ?? ""} onChange={(e) => updateField("priv_key_file", e.target.value || null)} />
              </Field>
              <Field label="CA List File" htmlFor="ca_list_file" info="Path to CA certificate bundle for client verification. Example: /etc/ssl/certs/ca-certificates.crt.">
                <Input id="ca_list_file" value={form.ca_list_file ?? ""} onChange={(e) => updateField("ca_list_file", e.target.value || null)} />
              </Field>
              <Field label="Cipher" htmlFor="cipher" info="TLS cipher suite string. Leave empty for PJSIP defaults. Example: ECDHE-RSA-AES256-GCM-SHA384.">
                <Input id="cipher" value={form.cipher ?? ""} onChange={(e) => updateField("cipher", e.target.value || null)} />
              </Field>
              <Field label="Method" htmlFor="method" info="TLS protocol version. tlsv1_2 recommended for modern security. Default (empty) uses system/PJSIP default.">
                <Select id="method" value={form.method ?? ""} onChange={(e) => updateField("method", e.target.value || null)}>
                  <option value="">Default</option>
                  <option value="tlsv1">TLSv1</option>
                  <option value="tlsv1_1">TLSv1.1</option>
                  <option value="tlsv1_2">TLSv1.2</option>
                </Select>
              </Field>
              <Field label="Verify Server" htmlFor="verify_server" info="Whether to verify the server certificate when connecting as a client. Default no. Enable for mutual TLS.">
                <Select id="verify_server" value={form.verify_server ?? ""} onChange={(e) => updateField("verify_server", e.target.value || null)}>
                  <option value="">Default</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Select>
              </Field>
              <div className="flex gap-6 mt-2 sm:col-span-2">
                <Switch id="allow_wildcard_certs" checked={form.allow_wildcard_certs} onChange={(e) => updateField("allow_wildcard_certs", e.target.checked)} label={<span>Allow Wildcard Certs <HelpIcon text="Accept certificates with wildcard CN/SAN entries. Disable for stricter security." /></span>} />
                <Switch id="require_client_cert" checked={form.require_client_cert} onChange={(e) => updateField("require_client_cert", e.target.checked)} label={<span>Require Client Cert <HelpIcon text="Require clients to present a valid certificate (mutual TLS). Enable for certificate-based authentication." /></span>} />
              </div>
            </FormSection>
          )}

          {showTcp && (
            <FormSection title="TCP keepalive" columns={1}>
              <div className="mt-2">
                <Switch id="tcp_keepalive_enable" checked={form.tcp_keepalive_enable} onChange={(e) => updateField("tcp_keepalive_enable", e.target.checked)} label={<span>TCP Keepalive <HelpIcon text="Enable TCP keepalive probes to detect dead connections. Recommended for TCP/TLS transports behind NAT." /></span>} />
                {form.tcp_keepalive_enable && (
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="tcp_keepalive_idle_time">Idle Time</Label>
                      <Input type="number" id="tcp_keepalive_idle_time" value={form.tcp_keepalive_idle_time} onChange={(e) => updateField("tcp_keepalive_idle_time", Number(e.target.value))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="tcp_keepalive_interval_time">Interval</Label>
                      <Input type="number" id="tcp_keepalive_interval_time" value={form.tcp_keepalive_interval_time} onChange={(e) => updateField("tcp_keepalive_interval_time", Number(e.target.value))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="tcp_keepalive_probe_count">Probe Count</Label>
                      <Input type="number" id="tcp_keepalive_probe_count" value={form.tcp_keepalive_probe_count} onChange={(e) => updateField("tcp_keepalive_probe_count", Number(e.target.value))} />
                    </div>
                  </div>
                )}
              </div>
            </FormSection>
          )}

          <FormSection title="Options" columns={1}>
            <Switch id="allow_reload" checked={form.allow_reload} onChange={(e) => updateField("allow_reload", e.target.checked)} label={<span>Allow Reload <HelpIcon text="Allow Asterisk reload to update this transport without a full restart. Disable for production stability." /></span>} />
            <Switch id="symmetric_transport" checked={form.symmetric_transport} onChange={(e) => updateField("symmetric_transport", e.target.checked)} label={<span>Symmetric Transport <HelpIcon text="Use the same socket for sending responses as receiving requests. Needed for NAT traversal when Asterisk receives on one port but sends from another." /></span>} />
          </FormSection>
        </div>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && handleDelete(pendingDelete)}
        title="Delete transport"
        description={pendingDelete ? `Delete transport "${pendingDelete.name}"? This cannot be undone.` : undefined}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
